import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, TrendingUp, Sparkles } from "lucide-react";
import PageHero from "../../components/PageHero";

const SLIDERS = [
  {
    key: "follower",
    label: "Wie viele Follower hast du aktuell?",
    min: 0,
    max: 100000,
    step: 500,
    default: 5000,
    color: "#ec4899",
    format: (v) => v.toLocaleString("de-DE"),
    leftLabel: "0",
    rightLabel: "100k+",
  },
  {
    key: "zufrieden",
    label: "Wie zufrieden bist du mit deinem Social Media aktuell?",
    min: 0,
    max: 100,
    step: 1,
    default: 50,
    color: "#a855f7",
    format: (v) => `${v}%`,
    leftLabel: "Gar nicht",
    rightLabel: "Sehr zufrieden",
  },
  {
    key: "budget",
    label: "Was gibst du für Social Media aus im Monat?",
    min: 0,
    max: 10000,
    step: 50,
    default: 1250,
    color: "#14b8a6",
    format: (v) => `${v.toLocaleString("de-DE")} €`,
    leftLabel: "0 €",
    rightLabel: "10.000 €+",
  },
  {
    key: "umsatz",
    label: "Wie viel verdienst du durch Social Media im Monat?",
    min: 0,
    max: 50000,
    step: 250,
    default: 2500,
    color: "#f97316",
    format: (v) => `${v.toLocaleString("de-DE")} €`,
    leftLabel: "0 €",
    rightLabel: "50.000 €+",
  },
  {
    key: "zeit",
    label: "Wie viele Stunden investierst du pro Woche?",
    min: 0,
    max: 40,
    step: 1,
    default: 8,
    color: "#3b82f6",
    format: (v) => `${v} h`,
    leftLabel: "0 h",
    rightLabel: "40+ h",
  },
  {
    key: "anfragen",
    label: "Wie viele Kunden-Anfragen kommen über Social Media?",
    min: 0,
    max: 100,
    step: 1,
    default: 5,
    color: "#22c55e",
    format: (v) => `${v} / Monat`,
    leftLabel: "0",
    rightLabel: "100+",
  },
];

const getResult = (values) => {
  // Calculate "score" based on inputs (lower satisfaction = lower score, lower ROI = lower score)
  const roi =
    values.budget > 0 ? values.umsatz / values.budget : values.umsatz / 100;
  const score =
    values.zufrieden * 0.4 +
    Math.min(roi * 10, 40) +
    Math.min(values.anfragen, 20);

  // No matter what → FREEAYTO is the conclusion
  if (score < 30) {
    return {
      level: "Akut: Hier liegt viel Potenzial brach",
      color: "#ec4899",
      bg: "linear-gradient(135deg, #ec4899, #f97316)",
      headline: "Du verschenkst Reichweite – und damit Umsatz.",
      diagnosis: [
        "Niedrige Zufriedenheit deutet auf fehlende Strategie hin",
        "Wenig Anfragen → Content erreicht nicht die richtigen Menschen",
        "Investierte Zeit zahlt sich kaum aus → Aufwand-Output passt nicht",
      ],
    };
  } else if (score < 60) {
    return {
      level: "Gut, aber ausbaufähig",
      color: "#f59e0b",
      bg: "linear-gradient(135deg, #f59e0b, #ec4899)",
      headline: "Solide Basis. Jetzt fehlt nur noch das System.",
      diagnosis: [
        "Du machst vieles richtig – aber unsystematisch",
        "Mit klarer Positionierung wären deine Zahlen 2–3x höher",
        "Performance-Logik (Hook → CTA) fehlt vermutlich",
      ],
    };
  } else {
    return {
      level: "Stark – jetzt skalieren",
      color: "#22c55e",
      bg: "linear-gradient(135deg, #22c55e, #14b8a6)",
      headline: "Du bist schon vorne dabei. Zeit, daraus mehr zu machen.",
      diagnosis: [
        "Du hast die Grundlagen verstanden – das ist schon viel",
        "Mit einem strategischen Partner kannst du jetzt skalieren",
        "Aus gutem Content wird durch System ein Verkaufs-Asset",
      ],
    };
  }
};

const CustomSlider = ({ slider, value, onChange }) => {
  const percent = ((value - slider.min) / (slider.max - slider.min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-base md:text-lg font-bold text-neutral-900">
          {slider.label}
        </label>
      </div>
      <div className="relative">
        <div className="h-2 rounded-full bg-neutral-200 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all"
            style={{ width: `${percent}%`, background: slider.color }}
          />
        </div>
        <input
          type="range"
          min={slider.min}
          max={slider.max}
          step={slider.step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border-[3px] shadow-lg pointer-events-none transition-all"
          style={{
            left: `calc(${percent}% - 12px)`,
            borderColor: slider.color,
          }}
        />
      </div>
      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="text-neutral-500 font-medium">{slider.leftLabel}</span>
        <span
          className="font-display font-extrabold text-xl tracking-tight"
          style={{ color: slider.color }}
        >
          {slider.format(value)}
        </span>
        <span className="text-neutral-500 font-medium">{slider.rightLabel}</span>
      </div>
    </div>
  );
};

const SocialMediaCheckPage = () => {
  const [values, setValues] = useState(
    SLIDERS.reduce((acc, s) => ({ ...acc, [s.key]: s.default }), {})
  );

  const result = useMemo(() => getResult(values), [values]);

  return (
    <>
      <PageHero
        eyebrow="Kostenloser Selbsttest"
        title="Wie gut ist dein"
        gradient="Social Media wirklich?"
        subtitle="Finde in 60 Sekunden heraus, wo du stehst – und wie FREEAYTO dich auf das nächste Level bringt. 6 Schieberegler, ehrliche Antwort."
      />

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="p-7 md:p-10 rounded-3xl bg-neutral-50 border border-black/5">
            <div className="flex items-center gap-2 mb-8">
              <Activity size={18} className="text-pink-500" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-700">
                Faktencheck
              </span>
            </div>

            <div className="space-y-10">
              {SLIDERS.map((s, idx) => (
                <div key={s.key}>
                  <div className="text-xs font-bold text-neutral-500 mb-1">
                    Frage {idx + 1} / {SLIDERS.length}
                  </div>
                  <CustomSlider
                    slider={s}
                    value={values[s.key]}
                    onChange={(v) => setValues({ ...values, [s.key]: v })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Result */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="relative p-8 md:p-12 rounded-[32px] overflow-hidden text-white" style={{ background: result.bg }}>
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} />
                <span className="text-xs uppercase tracking-[0.25em] font-bold opacity-90">
                  Dein Ergebnis
                </span>
              </div>
              <div className="text-sm font-semibold opacity-90 uppercase tracking-wide">
                {result.level}
              </div>
              <h2 className="mt-3 font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05]">
                {result.headline}
              </h2>
              <ul className="mt-6 space-y-2.5">
                {result.diagnosis.map((d, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-white/25 flex items-center justify-center text-[11px] font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-white/95">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solution = always FREEAYTO */}
          <div className="mt-5 p-8 md:p-10 rounded-[32px] bg-neutral-950 text-white relative overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.3),transparent_70%)]" />
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(249,115,22,0.25),transparent_70%)]" />
            <div className="relative">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-pink-400" />
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-300">
                  Die Lösung
                </span>
              </div>
              <h3 className="mt-3 font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em]">
                <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-purple-400 bg-clip-text text-transparent">
                  FREEAYTO.
                </span>
              </h3>
              <p className="mt-4 text-lg text-neutral-300 max-w-xl">
                Wir bauen für dich ein Social Media System, das aus Reichweite
                Anfragen macht. Positionierung, Content Creation, Betreuung und
                Performance – aus einer Hand.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/kontakt"
                  className="group inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-white text-neutral-950 text-sm font-bold tracking-[0.12em] uppercase hover:bg-gradient-to-r hover:from-pink-400 hover:via-orange-400 hover:to-purple-400 hover:text-white transition-all duration-300"
                >
                  Kostenloses Erstgespräch
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
                <Link
                  to="/leistungen"
                  className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full border-2 border-white/20 text-white text-sm font-bold tracking-[0.12em] uppercase hover:bg-white/10 transition-all duration-300"
                >
                  Leistungen ansehen
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SocialMediaCheckPage;
